import { datadogRum } from "@datadog/browser-rum";

export type LogLevel = "debug" | "info" | "warn" | "error";

export type ClientLogsConfig = {
  enabled: boolean;
  level: LogLevel;
};

// Ordered least to most severe, so the index doubles as the level threshold.
const LEVELS: LogLevel[] = ["debug", "info", "warn", "error"];
const CONSOLE_METHOD = {
  debug: "log",
  info: "log",
  warn: "warn",
  error: "error",
} as const;
const OVERRIDE_KEY = "uppLogs";

// Off until configureClientLogger runs. Production enablement lives in config.
let clientLogs: ClientLogsConfig = { enabled: false, level: "debug" };

const asLevel = (value: string | null): LogLevel | undefined =>
  LEVELS.find((level) => level === value);

// PX turns logs on mid-session with `?uppLogs=1` (or `?uppLogs=warn`). The
// level is kept per tab because in-app navigation drops the query string.
const readOverride = (): LogLevel | undefined => {
  const param = new URLSearchParams(window.location.search).get(OVERRIDE_KEY);
  const requested = asLevel(param === "1" ? "debug" : param);

  try {
    if (requested) {
      window.sessionStorage.setItem(OVERRIDE_KEY, requested);
    }
    return requested ?? asLevel(window.sessionStorage.getItem(OVERRIDE_KEY));
  } catch {
    // Private browsing can block storage; this page still logs.
    return requested;
  }
};

export const configureClientLogger = (config: ClientLogsConfig): void => {
  const override = readOverride();
  clientLogs = override ? { enabled: true, level: override } : config;

  if (override) {
    console.log(`[UPP] client logs enabled (${override})`);
  }
};

export const logMsg = (
  level: LogLevel,
  namespace: string,
  message: string,
  meta: Record<string, unknown> = {}
): void => {
  if (
    clientLogs.enabled &&
    LEVELS.indexOf(level) >= LEVELS.indexOf(clientLogs.level)
  ) {
    const details = Object.entries(meta)
      .map(([key, value]) => ` ${key}=${String(value)}`)
      .join("");
    console[CONSOLE_METHOD[level]](`[UPP][${namespace}] ${message}${details}`);
  }

  if (level === "error") {
    datadogRum.addError(new Error(message), { namespace, ...meta });
  }
};
